import classNames from 'classnames';
import Clipboard from 'clipboard';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties, ReactElement } from 'react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { PopupProps, SemanticICONS } from 'semantic-ui-react';
import { Button, Label, Icon, Popup } from 'semantic-ui-react';

import raiseToast from '@components/toast';

import s from './copy-button.scss';

interface CopyButtonProps {
  content?: string | null; // CopyButton is disabled when value is null or undefined
  message: string | ReactElement;
  corner?: boolean;
  icon?: boolean;
  /**
   * When true, replaces the copy icon with a green checkmark
   * for 2 seconds on success instead of showing a toast notification.
   */
  inlineConfirmation?: boolean;
  className?: string;
  style?: CSSProperties;
  popupProps?: PopupProps;
  /**
   * Glyph for the icon variant — either a Semantic UI icon name or a rendered
   * icon element (e.g. a Lucide icon). Defaults to the Semantic copy icon.
   * @default 'copy'
   */
  iconName?: SemanticICONS | ReactElement;
  /**
   * Accessible label for the icon variant.
   */
  ariaLabel?: string;
  /**
   * Hover tooltip text for the icon variant, before a copy.
   *
   * @default 'Copy to clipboard'
   */
  tooltip?: string;
}

const ToastMessage = ({ message }: { message: CopyButtonProps['message'] }) =>
  typeof message === 'string' ? (
    <span>
      {message.length
        ? message
        : 'This item has been successfully copied to your clipboard.'}
    </span>
  ) : (
    message
  );

export const CopyButton = ({
  content = null,
  message = '',
  corner = false,
  icon = false,
  inlineConfirmation = false,
  className,
  style,
  popupProps,
  iconName = 'copy',
  ariaLabel,
  tooltip = 'Copy to clipboard',
}: CopyButtonProps) => {
  useStyles(s);

  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleInlineCopy = useCallback(() => {
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1000);
  }, []);

  // Clear timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Use clipboard and on unmount, clean it up
  useEffect(() => {
    const clipboard = new Clipboard(`.${s.copy}`);
    return () => clipboard.destroy();
  }, []);

  const handleIconCopy = () => {
    if (inlineConfirmation) {
      handleInlineCopy();
    } else {
      raiseToast({
        message: <ToastMessage message={message} />,
        level: 'info',
        autoClose: 8000,
        icon: 'copy',
        dismissAll: true,
      });
    }
  };

  const iconClassName = classNames(s.copy, s.root, s.icon, className);

  // Clone a passed Lucide element so it carries the clipboard wiring that
  // clipboard.js resolves via the `s.copy` selector and `data-clipboard-text`.
  const iconTrigger =
    !copied && isValidElement(iconName) ? (
      cloneElement(iconName as ReactElement<Record<string, unknown>>, {
        className: classNames(
          iconClassName,
          (iconName.props as { className?: string }).className,
        ),
        'data-clipboard-text': content || '',
        'aria-label': ariaLabel,
        'aria-disabled': !content,
        style,
        onClick: handleIconCopy,
      })
    ) : (
      <Icon
        link
        color={copied ? 'green' : 'teal'}
        name={copied ? 'check circle' : (iconName as SemanticICONS)}
        className={iconClassName}
        data-clipboard-text={content || ''}
        aria-label={ariaLabel}
        style={style}
        onClick={handleIconCopy}
        disabled={!content}
        aria-disabled={!content}
      />
    );

  return icon ? (
    <Popup
      position="top center"
      trigger={iconTrigger}
      content={copied ? 'Copied!' : tooltip}
      {...popupProps}
      {...(inlineConfirmation && copied ? { open: true } : {})}
      disabled={!content}
      aria-disabled={!content}
    />
  ) : (
    <Button
      as={Label}
      onClick={() => {
        raiseToast({
          message: <ToastMessage message={message} />,
          level: 'info',
          autoClose: 8000,
          icon: 'copy',
          dismissAll: true,
        });
      }}
      basic
      className={classNames(
        s.copy,
        s.root,
        corner ? `${s.corner} darkCornerFix` : '',
        className,
      )}
      data-clipboard-text={content || ''}
      style={{ cursor: 'pointer', ...style }}
      disabled={!content}
      aria-disabled={!content}
    >
      <span>
        <Icon color="teal" name="copy" />
        Copy
      </span>
    </Button>
  );
};

export default CopyButton;
